/**
 * Chat Async Thunks
 * All async operations for the chat system
 */

import { createAsyncThunk } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';
import { RootState } from '../../store';
import {
  fetchUserConversations,
  fetchUserConversationsMetadata,
  fetchMoreUserConversationsMetadata,
  fetchConversationMessages,
  deleteConversation
} from '../../../services/conversationService';
import { ConversationPaginationResult } from '../../../models/Message';
import { deleteMessageAndSubsequent } from '../../../services/messageService';
import { getValidToken } from '../userSlice';

// Import from new modular files
import { extractUserIdFromToken } from './jwtUtils';
import { sendMessageToBackend, SendMessageApiParams } from './chatApiService';
import { setupConversationsListener } from './chatListeners';
import {
  Message,
  Conversation,
  SendMessagePayload,
  FetchConversationMessagesParams,
  DeleteConversationParams,
  DeleteMessageParams,
  StartListeningParams,
} from './chatTypes';

/**
 * Async thunk to fetch all chat history for the current user.
 * This uses the new user-centric data structure.
 */
export const fetchChatHistory = createAsyncThunk<Conversation[], string>(
  'chat/fetchHistory',
  async (userToken, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const userId = state.user.user?.id;

      if (!userId) {
        throw new Error('No authenticated user found');
      }

      // Use the service to fetch all conversations
      const conversations = await fetchUserConversations(userId);
      return conversations;
    } catch (error: any) {
      console.error('Error fetching chat history:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to send a message to the backend and handle the response.
 */
export const sendMessage = createAsyncThunk<void, SendMessagePayload>(
  'chat/sendMessage',
  async (
    payload,
    { dispatch, getState, rejectWithValue }
  ) => {
    try {
      // Extract payload properties
      const {
        message,
        inputText,
        isFirstMessage,
        extra_parameters,
        thinkingMode,
        files
      } = payload;

      // Get a fresh token only if needed
      const token = await dispatch(getValidToken()).unwrap();
      
      const state = getState() as RootState;
      const currentConversationId = state.chat?.currentConversationId;
      const userId = state.user.user?.id;

      if (!userId) {
        throw new Error('No authenticated user found');
      }

      // Generate a new message ID
      const id = message.id;
      let conversationId;
      if (isFirstMessage) {
        conversationId = currentConversationId || id;  // Use existing conversation ID or fallback to message ID
      } else {
        conversationId = currentConversationId;
      }

      // Add the user message to the state with the conversation ID
      const messageWithContext = {
        ...message,
        conversationId,
      };

      // Import actions from chatSlice
      const { setCurrentConversation, addMessage, setMessageLoading, setChatStatus, setMessageError } = await import('./chatSlice');

      // If this is a new conversation, set it as current
      if (!currentConversationId) {
        dispatch(setCurrentConversation(id));
      }
      
      // Add the message to the local state immediately
      dispatch(addMessage(messageWithContext));
      dispatch(setMessageLoading(message.id));
      dispatch(setChatStatus('busy'));
      
      // Prepare API parameters
      const apiParams: SendMessageApiParams = {
        inputText,
        isFirstMessage,
        currentConversationId,
        id,
        thinkingMode,
        extra_parameters,
        attachments: message.attachments, // Include attachments for backend persistence
      };

      // Send to backend using API service (multipart when files present)
      await sendMessageToBackend(apiParams, token, files);
      
      // We no longer need to manually fetch conversations after sending a message
      // Firebase listeners in ChatPage will automatically update when the backend processes the message
      // This eliminates the race condition and provides a more reactive UI

    } catch (error: any) {
      console.error('Error in sendMessage:', error);
      const { setMessageError, setChatStatus } = await import('./chatSlice');
      dispatch(setMessageError(payload.message.id));
      dispatch(setChatStatus('idle'));
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to fetch only conversation metadata for the current user.
 * This is more efficient for initial loading.
 */
export const fetchConversationsMetadata = createAsyncThunk<ConversationPaginationResult, string>(
  'chat/fetchConversationsMetadata',
  async (userToken, { dispatch, rejectWithValue }) => {
    try {
      // Ensure token is valid - we still accept the userToken for API compatibility
      await dispatch(getValidToken());
      
      // Get user ID from the token using the utility
      const userId = extractUserIdFromToken(userToken);

      // Fetch conversations metadata (without messages) - now returns pagination result
      const paginationResult = await fetchUserConversationsMetadata(userId, 20); // Initial page size of 20
      return paginationResult;
    } catch (error: any) {
      console.error('Error fetching conversations metadata:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to fetch more conversations for pagination.
 * This appends additional conversations to the existing array.
 */
export const fetchMoreConversations = createAsyncThunk<ConversationPaginationResult, { userToken: string; pageSize?: number }>(
  'chat/fetchMoreConversations',
  async ({ userToken, pageSize = 20 }, { dispatch, getState, rejectWithValue }) => {
    try {
      await dispatch(getValidToken());
      const userId = extractUserIdFromToken(userToken);
      
      const state = getState() as RootState;
      const { lastTimestamp } = state.chat.pagination;
      
      if (!lastTimestamp) {
        throw new Error('No timestamp available for pagination');
      }
      
      // Return the result - let the reducer handle state updates
      return await fetchMoreUserConversationsMetadata(userId, pageSize, lastTimestamp);
    } catch (error: any) {
      console.error('Error fetching more conversations:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Start listening for new conversations using the optimized listener management
 */
export const startListeningForConversations = createAsyncThunk<
  void,
  StartListeningParams
>(
  'chat/startListeningForConversations',
  async ({ userToken, callback }, { dispatch, getState, rejectWithValue }) => {
    try {
      // Ensure token is valid before setting up listeners
      await dispatch(getValidToken());
      
      // Get user ID from token using the utility
      const userId = extractUserIdFromToken(userToken);

      // Get current pagination state to determine timestamp filter
      const state = getState() as RootState;
      const fromTimestamp = state.chat.pagination.lastTimestamp;

      // Set up listener using the new optimized listener management
      const wrappedCallback = (conversations: Conversation[]) => {
        // Replace entire conversations array with the updated, sorted array
        dispatch({ 
          type: 'chat/replaceConversations', 
          payload: conversations 
        });
        
        // Call the original callback with the conversations
        callback(conversations);
      };

      // In chatThunks.ts - ONE place to handle the timestamp adjustment
      const adjustedTimestamp = fromTimestamp 
        ? new Date(new Date(fromTimestamp).getTime() - 1).toISOString()
        : undefined;

      // Use the new listener management system with automatic cleanup
      setupConversationsListener({
        userId,
        callback: wrappedCallback,
        fromTimestamp: adjustedTimestamp, // ← Adjusted once, works for both reflection & fraktal
      });

      // Listener is now managed by chatListenerManager with proper cleanup
    } catch (error: any) {
      console.error('Error starting conversation listener:', error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to fetch messages for a specific conversation.
 * Call this when a user selects a conversation to view.
 */
export const fetchConversationMessagesThunk = createAsyncThunk<
  Message[], 
  FetchConversationMessagesParams
>(
  'chat/fetchConversationMessages',
  async ({ userToken, conversationId, type }, { dispatch, rejectWithValue }) => {
    try {
      // Use the getValidToken utility instead of refreshing directly
      // We still accept userToken param to maintain API compatibility
      await dispatch(getValidToken());
      
      // Get user ID from the token using the utility
      const userId = extractUserIdFromToken(userToken);

      // Fetch messages for the selected conversation
      const messages = await fetchConversationMessages(userId, conversationId, type);
      return messages;
    } catch (error: any) {
      console.error(`Error fetching messages for conversation ${conversationId}:`, error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to delete a conversation.
 */
export const deleteConversationThunk = createAsyncThunk<
  void,
  DeleteConversationParams
>(
  'chat/deleteConversation',
  async ({ userToken, conversationId, type }, { dispatch, getState, rejectWithValue }) => {
    try {
      // Get a valid token - this will refresh only if needed
      await dispatch(getValidToken());
      
      // Get user ID from the token using the utility
      const userId = extractUserIdFromToken(userToken);

      // Call the delete function
      await deleteConversation(userId, conversationId, type);
      
      // Get current state
      const state = getState() as RootState;
      const currentConversationId = state.chat.currentConversationId;
      
      // Import actions from chatSlice
      const { clearChat, removeConversation } = await import('./chatSlice');
      
      // If the deleted conversation was the current one, clear the chat
      if (currentConversationId === conversationId) {
        dispatch(clearChat(false));
      }
      
      // Also manually remove the conversation from the Redux store
      // this ensures UI updates immediately without waiting for Firebase listener
      dispatch(removeConversation(conversationId));
      
      return;
    } catch (error: any) {
      console.error(`Error deleting conversation ${conversationId}:`, error);
      return rejectWithValue(error.message);
    }
  }
);

/**
 * Async thunk to delete a message and all subsequent messages.
 */
export const deleteMessageThunk = createAsyncThunk<
  void,
  DeleteMessageParams
>(
  'chat/deleteMessage',
  async ({ userToken, conversationId, messageId, createdAt, type }, { dispatch, getState, rejectWithValue }) => {
    try {
      // Get a valid token - this will refresh only if needed
      await dispatch(getValidToken());
      
      // Get user ID from the token using the utility
      const userId = extractUserIdFromToken(userToken);

      // Get current state
      const state = getState() as RootState;
      const messages = state.chat.messages;
      
      // Find subsequent messages to remove from Redux state
      const messageIndex = messages.findIndex((m: Message) => m.id === messageId);
      if (messageIndex === -1) {
        console.error(`Message ${messageId} not found in state`);
        return;
      }
      
      // Call the delete function
      await deleteMessageAndSubsequent(
        userId, 
        conversationId, 
        messageId, 
        createdAt,
        type
      );
      
      // Import actions from chatSlice
      const { removeMessagesAfterTimestamp } = await import('./chatSlice');
      
      // Remove the deleted messages from local state
      dispatch(removeMessagesAfterTimestamp(createdAt));
      
      return;
    } catch (error: any) {
      console.error(`Error deleting message ${messageId}:`, error);
      return rejectWithValue(error.message);
    }
  }
); 